import { isCustomAuthConfigured } from "./cognitoAuth";

const config = window.pcaSettings;
const webUri = `${window.location.protocol}//${window.location.host}/`;
const loginUrl = `${config.auth.uri}/login?client_id=${config.auth.clientId}&response_type=code&redirect_uri=${webUri}`;

export function redirectToLogin(message, err) {
  console.debug("Redirect to login:", message, err);

  const path = window.location.pathname + window.location.search;
  if (path && path !== '/') {
    sessionStorage.setItem('auth_return_path', path);
  }

  // A rejected token (e.g. left over from a different pool/client config, or
  // genuinely revoked) must be cleared here. getToken() only checks the exp
  // claim client-side — it can't tell a token like that from a good one — so
  // without this, reloading to the sign-in card just re-reads the same bad
  // token, retries the request, gets rejected again, and reloads again in an
  // infinite loop instead of ever reaching the login form.
  localStorage.removeItem('id_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  // The next session may come from the other sign-in path — don't let a
  // stale token-type preference misroute it.
  localStorage.removeItem('auth_token_pref');

  // With a user pool configured, the sign-in card the app already renders at
  // startup (see index.js) is where users belong — not the AWS-hosted page
  // the custom flow exists to replace.
  if (isCustomAuthConfigured()) {
    window.location.assign(webUri);
    return;
  }

  console.debug("Login URL is:", loginUrl);
  window.location.href = loginUrl;
}

export function parseAuthQueryString() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const error = params.get("error");
  if (error) {
    throw new Error(params.get("error_description") || "Invalid configuration");
  }
  return code;
}

export async function handleCode(code) {
  console.debug("Exchanging code for token:", code);

  let data;
  try {
    data = await authRequest("authorization_code", {
      redirect_uri: webUri,
      code: code,
    });
  } catch (err) {
    return redirectToLogin("Couldn't validate code", err);
  }

  store(data);

  // Restore the page the user was on before auth redirect, then remove ?code=
  const returnPath = sessionStorage.getItem('auth_return_path');
  sessionStorage.removeItem('auth_return_path');
  const url = new URL(window.location);
  url.searchParams.delete("code");
  if (returnPath) {
    url.pathname = returnPath.split('?')[0];
    url.search = returnPath.includes('?') ? returnPath.slice(returnPath.indexOf('?')) : '';
  }
  window.history.pushState({}, "", url);
}

async function authRequest(grant_type, data) {
  console.debug("Doing", grant_type, " with", data);
  let body = new URLSearchParams();
  body.append("grant_type", grant_type);
  body.append("client_id", config.auth.clientId);
  Object.keys(data).forEach((key) => {
    body.append(key, data[key]);
  });

  const url = `${config.auth.uri}/oauth2/token`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body,
  });

  if (!response.ok) {
    console.debug(response);
    throw new Error("Bad response from auth endpoint");
  }

  return response.json();
}

export async function getToken() {
  const token = selectAuthToken();

  if (token === null) {
    return;
  }

  try {
    const payload = payloadFromToken(token);
    if (Math.floor(Date.now() / 1000) < payload.exp) {
      return token;
    }
  } catch (err) {
    console.debug(err);
  }

  // Refresh tokens
  console.debug("Expired token");

  try {
    return await refreshToken();
  } catch (err) {
    return redirectToLogin("Error refreshing tokens", err);
  }
}

// Picks the Authorization token matching this stored session's origin, so one
// frontend build works against either authorizer mode. A COGNITO_USER_POOLS
// authorizer with AuthorizationScopes configured (access-token mode, e.g.
// [openid]) only accepts access tokens carrying that scope; one with none
// configured (ID-token mode) only accepts ID tokens. The stored access
// token's own `scope` claim is the discriminator: Hosted-UI/OAuth sessions
// request `openid` (see PCAUserPoolClient.AllowedOAuthScopes in
// cognito.template), while the custom SRP form never goes through OAuth, so
// its access tokens only ever carry the reserved
// aws.cognito.signin.user.admin scope.
// The origin-based default can still mismatch the backend (e.g. a Hosted-UI
// session against a blank-scopes ID-token-mode authorizer). When the API 401s
// the selected token, tryFallbackTokenMode() below records the working
// alternative here so the app adapts to whatever backend is deployed.
const TOKEN_PREF_KEY = "auth_token_pref";

const accessTokenHasOpenid = (accessToken) => {
  try {
    const { scope } = payloadFromToken(accessToken);
    return typeof scope === "string" && scope.split(" ").includes("openid");
  } catch (err) {
    console.debug(err);
    return false;
  }
};

function selectAuthToken() {
  const idToken = window.localStorage.getItem("id_token");
  const accessToken = window.localStorage.getItem("access_token");

  const pref = window.localStorage.getItem(TOKEN_PREF_KEY);
  if (pref === "id" && idToken) return idToken;
  if (pref === "access" && accessToken) return accessToken;

  if (accessToken && accessTokenHasOpenid(accessToken)) {
    return accessToken;
  }
  return idToken;
}

// Called by api.js on a 401: switches to the stored session's other token
// type, if one exists that could plausibly be accepted, and returns whether a
// retry is worthwhile. The preference sticks (localStorage) so the switch
// costs one extra round trip per browser, not one per request.
export function tryFallbackTokenMode() {
  const idToken = window.localStorage.getItem("id_token");
  const accessToken = window.localStorage.getItem("access_token");
  const current = selectAuthToken();

  if (current && current === accessToken && idToken) {
    window.localStorage.setItem(TOKEN_PREF_KEY, "id");
    return true;
  }
  if (
    current &&
    current === idToken &&
    accessToken &&
    accessTokenHasOpenid(accessToken) &&
    window.localStorage.getItem(TOKEN_PREF_KEY) !== "access"
  ) {
    window.localStorage.setItem(TOKEN_PREF_KEY, "access");
    return true;
  }
  return false;
}

export function payloadFromToken(token) {
  const parts = token.split(".");

  if (parts.length !== 3) throw new Error("Invalid token");

  // JWTs are base64url, not base64 — atob rejects the '-' and '_' it uses in
  // place of '+' and '/', which a Cognito token's payload segment contains
  // often enough that this was failing on nearly every real token.
  const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");

  return JSON.parse(window.atob(padded));
}

// Concurrent callers (several API calls firing right after app mount) must
// share one in-flight refresh rather than each racing their own — the old
// code cleared the stored tokens before its network call resolved, so a
// second caller reading localStorage mid-refresh saw nothing and got sent
// back through sign-in.
let pendingRefresh = null;

export async function refreshToken() {
  if (pendingRefresh) return pendingRefresh;

  pendingRefresh = doRefresh().finally(() => {
    pendingRefresh = null;
  });
  return pendingRefresh;
}

async function doRefresh() {
  let data = await authRequest("refresh_token", {
    refresh_token: window.localStorage.getItem("refresh_token"),
  });
  console.debug("Tokens refreshed");

  store(data);

  // Re-select rather than hardcode: a refreshed session keeps sending
  // whichever token type matches its actual origin (see selectAuthToken).
  return selectAuthToken();
}

function store(data) {
  window.localStorage.setItem("id_token", data.id_token);
  window.localStorage.setItem("access_token", data.access_token);
  // Cognito's refresh-token grant response contains no refresh_token —
  // writing it unconditionally stored the string "undefined", breaking the
  // next refresh. Keep the existing one unless a new one is issued.
  if (data.refresh_token) {
    window.localStorage.setItem("refresh_token", data.refresh_token);
  }
}

export function logOut() {
  console.log("logout clicked");
  localStorage.removeItem("id_token");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  redirectToLogin("user logged out");
}

export function getAuthHeaders() {
  // Must match getToken(): same per-session token selection.
  const token = selectAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

